import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { useCurrency } from "@/contexts/CurrencyContext";
import { applySEO, SITE_URL, breadcrumbSchema } from "@/lib/seo";

function ytId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default function LookDetail() {
  const { slug } = useParams();
  const [look, setLook] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currency, formatAmount } = useCurrency();

  useEffect(() => {
    setLoading(true);
    api.get(`/looks/${slug}`).then((r) => {
      setLook(r.data);
      applySEO({
        title: r.data.seo_title || `${r.data.title} — Lookbook | Asymmetry Edits`,
        description: r.data.seo_description || r.data.description?.slice(0, 158),
        canonical: `${SITE_URL}/lookbook/${r.data.slug}`,
        image: r.data.cover_image,
        og: { type: "article" },
        jsonLd: [{
          id: "ld-look-crumb",
          data: breadcrumbSchema([{name:"Home",url:"/"},{name:"Lookbook",url:"/lookbook"},{name:r.data.title,url:`/lookbook/${r.data.slug}`}])
        }],
      });
    }).catch(()=>setLook(null)).finally(()=>setLoading(false));
    window.scrollTo(0,0);
  }, [slug]);

  if (loading) return <div className="bg-midnight min-h-screen"><Navbar /></div>;
  if (!look) return <div className="bg-midnight min-h-screen"><Navbar /><div className="pt-40 text-center"><h2 className="font-serif text-white text-4xl">Look not found.</h2><Link to="/lookbook" className="mt-6 inline-block text-silver text-[11px] tracking-[0.32em] uppercase border-b border-silver">Back to Lookbook</Link></div></div>;

  const yt = ytId(look.video_url);

  return (
    <div className="bg-midnight min-h-screen">
      <Navbar />
      <main data-testid="look-detail-page" className="pt-28 md:pt-32 pb-20">
        <div className="px-6 md:px-12 lg:px-24 mb-8">
          <Link to="/lookbook" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[11px] tracking-[0.3em] uppercase"><ArrowLeft size={14}/>Back to Lookbook</Link>
        </div>

        <section className="relative aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden">
          <img src={look.cover_image} alt={look.alt||look.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b10] via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-24">
            <div className="eyebrow mb-3">{look.collection_name}{look.season?` · ${look.season}`:""}{look.year?` · ${look.year}`:""}</div>
            <h1 className="font-serif text-white text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">{look.title}</h1>
            {look.description && <p className="mt-4 max-w-xl text-white/70 font-light">{look.description}</p>}
          </div>
        </section>

        <div className="px-6 md:px-12 lg:px-24 py-12 md:py-20 space-y-16">
          {(look.tags||[]).length>0 && <div className="flex flex-wrap gap-2">{look.tags.map(t=><span key={t} className="text-[10px] tracking-[0.28em] uppercase text-white/60 border border-white/15 px-3 py-1.5">{t}</span>)}</div>}

          {yt && <div className="aspect-video border border-white/8"><iframe src={`https://www.youtube.com/embed/${yt}`} title="Look video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full"/></div>}

          {(look.gallery||[]).length>0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {look.gallery.map((g,i) => <div key={i} className="aspect-[3/4] overflow-hidden bg-midnight-panel"><img src={g} alt={`${look.title} ${i+1}`} loading="lazy" className="w-full h-full object-cover"/></div>)}
            </div>
          )}

          {(look.products||[]).length>0 && (
            <section>
              <div className="flex items-end justify-between border-b border-white/8 pb-6 mb-8">
                <div><div className="eyebrow text-silver/70 mb-2">Shop this Look</div><h2 className="font-serif text-white text-3xl">Products in this edit.</h2></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
                {look.products.map(p => (
                  <Link key={p.id} to={`/product/${p.id}`} data-testid={`look-product-${p.id}`} className="group block">
                    <div className="aspect-[3/4] overflow-hidden bg-midnight-panel mb-3"><img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover product-img"/></div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70">{p.category}</div>
                    <div className="font-serif text-white text-lg mt-1 leading-tight">{p.name}</div>
                    <div className="text-silver text-sm mt-1">{formatAmount(currency==="INR"?p.price_inr:p.price_usd)}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {(look.photographer || look.model) && (
            <section className="border-t border-white/8 pt-8 text-[10px] tracking-[0.35em] uppercase text-white/55 flex flex-wrap gap-x-10 gap-y-2">
              {look.photographer && <div><span className="text-silver/70 mr-3">Photography</span>{look.photographer}</div>}
              {look.model && <div><span className="text-silver/70 mr-3">Model</span>{look.model}</div>}
            </section>
          )}
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
